import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { clearSiteContent } from "../../lib/siteContent";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";

const TitleInputs = ({ d, setD, k, en, fr }) => (
  <div className="grid sm:grid-cols-2 gap-3 -mt-2">
    {[["en", "Heading (English)", en], ["fr", "Heading (French)", fr]].map(([l, label, ph]) => (
      <label key={l} className="block">
        <span className="text-xs font-semibold text-slate-600">{label}</span>
        <Input value={d[`${k}_title_${l}`] || ""} maxLength={60} placeholder={ph} data-testid={`${k}-title-${l}`}
          onChange={(e) => setD({ ...d, [`${k}_title_${l}`]: e.target.value })} className="mt-1" />
      </label>
    ))}
  </div>
);
import { toast } from "sonner";
import { RotateCcw, Save } from "lucide-react";

const ROWS = [
  ["title", "Box heading"],
  ["pet", "Pet ads (dogs, cats, birds, reptiles, aquatics, small pets, livestock, rescue)"],
  ["supplies-services", "Supplies & services"],
  ["lost-found", "Lost & Found"],
  ["iso", "Wanted (ISO)"],
];

export function SafetyNotesSettings() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/site/safety-notes").then((r) => setD(r.data)).catch((e) => toast.error(errText(e))); }, []);
  if (!d) return null;
  const set = (lang, k, v) => setD({ ...d, [lang]: { ...d[lang], [k]: v } });
  const save = () => {
    setBusy(true);
    api.put("/admin/site/safety-notes", { en: d.en, fr: d.fr })
      .then((r) => { setD({ ...d, ...r.data }); clearSiteContent("safety"); toast.success("Safety wording saved"); })
      .catch((e) => toast.error(errText(e))).finally(() => setBusy(false));
  };
  const reset = () => setD({ ...d, en: { ...d.defaults.en }, fr: { ...d.defaults.fr } });
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5" data-testid="safety-notes-settings">
      <div>
        <h3 className="font-display text-lg font-bold text-[#12234A]">Safety First box</h3>
        <p className="text-sm text-slate-500">Shown on every ad page beside the seller's contact buttons. A blank French field shows the English wording.</p>
      </div>
      {ROWS.map(([k, label]) => (
        <div key={k} className="space-y-2">
          <p className="text-sm font-semibold text-[#12234A]">{label}</p>
          <div className="grid md:grid-cols-2 gap-3">
            {["en", "fr"].map((lang) => (
              <label key={lang} className="block text-xs text-slate-500">{lang === "en" ? "English" : "French"}
                {k === "title"
                  ? <Input value={d[lang][k] || ""} maxLength={40} onChange={(e) => set(lang, k, e.target.value)} className="mt-1" data-testid={`safety-${lang}-${k}`} />
                  : <Textarea value={d[lang][k] || ""} maxLength={300} rows={2} onChange={(e) => set(lang, k, e.target.value)} className="mt-1" data-testid={`safety-${lang}-${k}`} />}
              </label>
            ))}
          </div>
        </div>
      ))}
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={reset} data-testid="safety-reset" className="gap-1.5"><RotateCcw size={15} /> Restore default wording</Button>
        <Button onClick={save} disabled={busy} data-testid="safety-save" className="bg-[#F5551E] hover:bg-[#e04a15] gap-1.5"><Save size={15} /> Save</Button>
      </div>
    </div>
  );
}

export function HomeFeedSettings() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/site/home-feed").then((r) => setD(r.data)).catch((e) => toast.error(errText(e))); }, []);
  if (!d) return null;
  const save = () => {
    setBusy(true);
    api.put("/admin/site/home-feed", d).then((r) => { setD(r.data); clearSiteContent("home_feed"); toast.success("Feed settings saved"); })
      .catch((e) => toast.error(errText(e))).finally(() => setBusy(false));
  };
  const Opt = ({ v, title, desc }) => (
    <button type="button" onClick={() => setD({ ...d, fresh_mode: v })} data-testid={`home-feed-mode-${v}`}
      className={`text-left rounded-xl border p-4 transition-colors ${d.fresh_mode === v ? "border-[#F5551E] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
      <p className="font-semibold text-[#12234A] text-sm">{title}</p>
      <p className="text-xs text-slate-500 mt-1">{desc}</p>
    </button>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5 max-w-2xl" data-testid="home-feed-settings">
      <div>
        <h3 className="font-display text-lg font-bold text-[#12234A]">Fresh listings: loading more</h3>
        <p className="text-sm text-slate-500">How visitors see more ads below the first 16 in Fresh listings on the homepage.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Opt v="button" title="Load more button" desc="Visitors tap a button to add more listings." />
        <Opt v="scroll" title="Automatic on scroll" desc="More listings appear as visitors scroll down. Stops at 48, then shows a View all button so the footer stays reachable." />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#12234A] mb-2">Listings added each time</p>
        <div className="flex gap-2">
          {[4, 8, 12].map((n) => (
            <button key={n} type="button" onClick={() => setD({ ...d, fresh_step: n })} data-testid={`home-feed-step-${n}`}
              className={`h-9 px-4 rounded-xl border text-sm font-semibold transition-colors ${d.fresh_step === n ? "border-[#F5551E] bg-orange-50 text-[#12234A]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {n} {n === 4 ? "(1 row)" : `(${n / 4} rows)`}
            </button>
          ))}
        </div>
      </div>
      <div className="pt-4 border-t border-slate-100">
        <h3 className="font-display text-lg font-bold text-[#12234A]">Browse page: more results</h3>
        <p className="text-sm text-slate-500 mb-3">How visitors move past the first 24 results on the Browse and search pages.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {[["pages", "Page numbers", "Prev / 1 2 3 / Next at the bottom (current)."],
            ["button", "Load more button", "Visitors tap to add the next 24 below."],
            ["scroll", "Automatic on scroll", "Next 24 appear as visitors scroll. Stops at 120, then shows a button."]].map(([v, title, desc]) => (
            <button key={v} type="button" onClick={() => setD({ ...d, browse_mode: v })} data-testid={`browse-mode-${v}`}
              className={`text-left rounded-xl border p-4 transition-colors ${(d.browse_mode || "pages") === v ? "border-[#F5551E] bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
              <p className="font-semibold text-[#12234A] text-sm">{title}</p>
              <p className="text-xs text-slate-500 mt-1">{desc}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold text-[#12234A]">Browse page: "You might also like" row</h3>
          <p className="text-sm text-slate-500">Suggests ads similar to the pets a visitor recently viewed, shown under the results.</p>
        </div>
        <Switch checked={d.similar_row !== false} onCheckedChange={(v) => setD({ ...d, similar_row: v })} data-testid="similar-row-toggle" />
      </div>
      <TitleInputs d={d} setD={setD} k="similar" en="You might also like" fr="Vous aimerez peut-être aussi" />
      <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold text-[#12234A]">"Recently viewed" carousel</h3>
          <p className="text-sm text-slate-500">Shows visitors the ads they opened recently, on the homepage and Browse pages.</p>
        </div>
        <Switch checked={d.recent_row !== false} onCheckedChange={(v) => setD({ ...d, recent_row: v })} data-testid="recent-row-toggle" />
      </div>
      <TitleInputs d={d} setD={setD} k="recent" en="Recently viewed" fr="Vus récemment" />
      <div className="flex justify-end"><Button onClick={save} disabled={busy} data-testid="home-feed-save" className="bg-[#F5551E] hover:bg-[#e04a15] gap-1.5"><Save size={15} /> Save</Button></div>
    </div>
  );
}

export function SiteEmailsSettings() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/site/emails").then((r) => setD(r.data)).catch((e) => toast.error(errText(e))); }, []);
  if (!d) return null;
  const save = () => {
    setBusy(true);
    api.put("/admin/site/emails", d).then((r) => { setD(r.data); clearSiteContent("emails"); toast.success("Email addresses saved"); })
      .catch((e) => toast.error(errText(e))).finally(() => setBusy(false));
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 max-w-2xl" data-testid="site-emails-settings">
      <div>
        <h3 className="font-display text-lg font-bold text-[#12234A]">Site email addresses</h3>
        <p className="text-sm text-slate-500">These addresses are shown to visitors across the site and in emails.</p>
      </div>
      <label className="block text-sm font-medium text-[#12234A]">General contact
        <span className="block text-xs font-normal text-slate-500">Contact Us, About, Trust & Safety, Privacy and the contact-form reply email</span>
        <Input type="email" value={d.contact} onChange={(e) => setD({ ...d, contact: e.target.value })} className="mt-1" data-testid="site-email-contact" />
      </label>
      <label className="block text-sm font-medium text-[#12234A]">Advertising payments
        <span className="block text-xs font-normal text-slate-500">Contact Us "Advertising" box, banner-ad e-Transfer instructions and receipts</span>
        <Input type="email" value={d.payment} onChange={(e) => setD({ ...d, payment: e.target.value })} className="mt-1" data-testid="site-email-payment" />
      </label>
      <p className="text-xs text-slate-400">The e-Transfer address for paid Featured Spots is set separately under Paid Featured Spots.</p>
      <div className="flex justify-end"><Button onClick={save} disabled={busy} data-testid="site-emails-save" className="bg-[#F5551E] hover:bg-[#e04a15] gap-1.5"><Save size={15} /> Save</Button></div>
    </div>
  );
}
